/**
 * Firebase 초기화 설정
 *
 * Firebase Authentication (Phone Provider) 사용
 * @see SPEC-001-firebase-phone-auth.md
 */
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, browserLocalPersistence, setPersistence, type Auth } from 'firebase/auth';
import { initializeAppCheck, ReCaptchaEnterpriseProvider, type AppCheck } from 'firebase/app-check';
import { logger } from '@/utils/logger';

/**
 * Firebase 설정
 * 환경변수에서 로드 (Vite는 VITE_ 접두사 사용)
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

/**
 * Firebase 설정 유효성 검사
 */
function validateFirebaseConfig(): boolean {
  const requiredFields = ['apiKey', 'authDomain', 'projectId', 'appId'];
  const missingFields = requiredFields.filter(
    (field) => !firebaseConfig[field as keyof typeof firebaseConfig]
  );

  if (missingFields.length > 0) {
    logger.error('[Firebase] Missing config fields:', missingFields);
    return false;
  }

  return true;
}

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let appCheck: AppCheck | null = null;
// INVISIBLE 타입 reCAPTCHA Enterprise 키 (Phone Auth invisible reCAPTCHA 호환)
const RECAPTCHA_ENTERPRISE_SITE_KEY = '6LdNJSksAAAAAJgcigjE8gJOS5hCgaAXGO3SVQJh';

/**
 * Firebase 앱 초기화
 * 싱글톤 패턴으로 한 번만 초기화
 */
export function initializeFirebase(): FirebaseApp | null {
  if (app) {
    return app;
  }

  if (!validateFirebaseConfig()) {
    logger.warn('[Firebase] Invalid configuration. Firebase will not be initialized.');
    return null;
  }

  try {
    app = initializeApp(firebaseConfig);
    logger.info('[Firebase] App initialized successfully');

    // App Check 초기화 (프로덕션 환경에서만 활성화)
    // 개발/스테이징 환경에서는 Firebase Console에서 App Check 설정 완료 후 활성화
    const isProduction = import.meta.env.VITE_SENTRY_ENVIRONMENT === 'production';
    if (isProduction) {
      try {
        appCheck = initializeAppCheck(app, {
          provider: new ReCaptchaEnterpriseProvider(RECAPTCHA_ENTERPRISE_SITE_KEY),
          isTokenAutoRefreshEnabled: true,
        });
        logger.info('[Firebase] App Check initialized successfully');
      } catch (appCheckError) {
        logger.warn('[Firebase] App Check initialization failed:', appCheckError);
      }
    } else {
      logger.info('[Firebase] App Check skipped (non-production environment)');
    }

    return app;
  } catch (error) {
    logger.error('[Firebase] Failed to initialize app:', error);
    return null;
  }
}

/**
 * Firebase Auth 인스턴스 가져오기
 */
export function getFirebaseAuth(): Auth | null {
  if (auth) {
    return auth;
  }

  const firebaseApp = initializeFirebase();
  if (!firebaseApp) {
    return null;
  }

  try {
    auth = getAuth(firebaseApp);
    // reCAPTCHA 에러 방지를 위한 언어 설정
    auth.languageCode = 'es';

    // 브라우저 종료 후에도 로그인 상태 유지 (#253)
    setPersistence(auth, browserLocalPersistence).catch((error) => {
      logger.warn('[Firebase] Failed to set persistence:', error);
    });

    return auth;
  } catch (error) {
    logger.error('[Firebase] Failed to get auth instance:', error);
    return null;
  }
}

/**
 * Auth 언어 설정 변경
 */
export function setAuthLanguage(language: string): void {
  if (auth) {
    auth.languageCode = language;
  }
}

/**
 * Firebase 초기화 상태 확인
 */
export function isFirebaseInitialized(): boolean {
  return app !== null;
}

/**
 * Firebase Auth 사용 가능 여부
 */
export function isFirebaseAuthAvailable(): boolean {
  return getFirebaseAuth() !== null;
}
